import React, { useEffect, useRef } from "react";
import { Offcanvas } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../nodes/Logic/Inputs/Input";
import timezones from "../../nodes/Logic/timezones";
import ExtractOptions from "../../nodes/Logic/Inputs/ExtractOptions";
import UsedInLink from "../UsedInLink";
import JsonCollapse from "../JsonCollapse";
import {
  addNewInputArgument,
  addNewVariableGroup,
  selectInputArgument,
  selectInputArguments,
  selectProjectTimezone,
  selectVariableGroup,
  setInputArgument,
  setVariableGroup,
} from "../../../store/slices/projectSlice";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { useGetContactFieldsQuery } from "../../../store/slices/contactsSlice";
import {
  closeModal,
  selectCurrentModal,
  selectModalData,
  setCurrentModal,
} from "../../../store/slices/modalsSlice";
import { useGetApiDataCollectionsQuery } from "../../../store/slices/settingsSlice";
import { useGetReportingTemplatesQuery } from "../../../store/slices/reportingSlice";
import { pageProps } from "../../../helpers/pageProps";
import VisibilityInputs from "./VisibilityInputs";

export default function VariableGroup() {
  const show = useSelector(selectCurrentModal) === "variableGroup";

  const { variableGroupUUID } = useSelector(selectModalData) || {};

  const editedVariable = useSelector(selectVariableGroup(variableGroupUUID));

  const dispatch = useDispatch();

  const setEditedVariable = (fn) => {
    if (typeof fn === "function") {
      dispatch(setVariableGroup(fn(editedVariable)));
    } else {
      dispatch(setVariableGroup(fn));
    }
  };

  const labelRef = useRef();

  const handleClose = () => {
    if (editedVariable.Label) {
      dispatch(closeModal());
    } else {
      toast.error("Label is missing.");
    }
  };

  useEffect(() => {
    // set edited variable when the window shows
    if (variableGroupUUID) {
      if (variableGroupUUID === "create_new") {
        const newIAUUID = uuidv4();
        dispatch(addNewVariableGroup({ GroupUuid: newIAUUID }));
        dispatch(
          setCurrentModal({
            modal: "variableGroup",
            data: { variableGroupUUID: newIAUUID },
          })
        );
      }
    }
  }, [variableGroupUUID]);

  return (
    editedVariable && (
      <div>
        <Offcanvas
          show={show}
          onHide={handleClose}
          onEntered={() => labelRef.current.select()}
          className="z-3000"
          backdropClassName="z-2999"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              <FontAwesomeIcon icon={faEdit} /> Edit variable group
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div>
              <div className="mb-3">
                <label htmlFor="inputVariableLabel" className="form-label">
                  Label
                </label>
                <input
                  ref={labelRef}
                  type="text"
                  placeholder="Variable label"
                  className="form-control"
                  id="inputVariableLabel"
                  value={editedVariable?.Label ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Label: e.target.value,
                    }))
                  }
                />
                {editedVariable && !editedVariable?.Label && (
                  <span className="text-danger">Need a label!</span>
                )}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="inputVariableDescription"
                  className="form-label"
                >
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Variable description"
                  className="form-control"
                  id="inputVariableDescription"
                  value={editedVariable?.Description ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Description: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-3">
                <label htmlFor="inputVariableOrder" className="form-label">
                  Order
                </label>
                <input
                  placeholder="Variable order"
                  className="form-control"
                  id="inputVariableOrder"
                  type="number"
                  value={editedVariable?.Order ?? ""}
                  onChange={(e) =>
                    setEditedVariable((prev) => ({
                      ...prev,
                      Order: parseInt(e.target.value, 10),
                    }))
                  }
                />
              </div>
              <VisibilityInputs
                editedVariable={editedVariable}
                setEditedVariable={setEditedVariable}
              />
            </div>
            <UsedInLink arg={editedVariable} />
            <JsonCollapse json={editedVariable} />
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    )
  );
}
